import { useEffect, useState } from "react";
import apiFetch from "../utils/apiFetch";

// How often to re-check the unread-messages count while the page stays open.
const POLL_INTERVAL_MS = 30000;

// Unread-messages count for the staff navbar badge. Messages have no websocket
// channel (the legacy app polled on route change), so this seeds from
// appProps.unread_messages_count for an instant first paint and then keeps the
// badge current with the check_for_unread_messages endpoint: once on mount, on a
// 30s interval, and whenever the tab regains focus. The v2 navbar persists across
// SPA navigations, so these are the natural refresh points.
export default function useUnreadMessagesCount(appProps, schoolSlug) {
  const [count, setCount] = useState(appProps?.unread_messages_count ?? 0);

  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      try {
        const response = await apiFetch(
          "/api/messages/check_for_unread_messages?recipient_type=Staff",
          { schoolSlug }
        );
        if (!response.ok) return;
        const payload = await response.json().catch(() => ({}));
        if (!cancelled && typeof payload.data === "number") {
          setCount(payload.data);
        }
      } catch {
        // Leave the last known count in place on a transient failure.
      }
    };

    const onFocus = () => {
      if (document.visibilityState === "visible") refresh();
    };

    refresh();
    const intervalId = setInterval(refresh, POLL_INTERVAL_MS);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [schoolSlug]);

  return count;
}
